import React from 'react';
import * as mobxReact from 'mobx-react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { CommonPopupBodyFooterLayout } from '../../../../global/common-popup/common-popup-body-footer-layout';
import { DefaultCloseSubWindowButton } from '../../../../global/sub-window/common/common-close-sub-window-buttons';
import { FormFieldInputSelect } from '../../../../global/form/form-field-input/form-field-input-select';
import { FormFieldInputText } from '../../../../global/form/form-field-input/form-field-input-text';
import { FormFieldInputTextArea } from '../../../../global/form/form-field-input/form-field-input-texarea';
import DefaultButton from '../../../../global/default-button/default-button';
import OECheckbox from '../../../../global/oe-checkbox/oe-checkbox';
import { OERadioOptions } from '../../../../global/oe-radio-options/oe-radio-options';
import { MaterialSpinner } from '../../../../global/spinner/material-spinner';
import { Tooltip } from '../../../../global/tooltip/tooltip';
import NoteIcon from '../../../../../images/svg/sf-order-note';
import { FormFieldProps } from '../../../../../infrastructure/form-viewmodel-controller';
import { hasPermission } from '../../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../../api/roles.dao';
import { APPLY_TEXT, dataIsBeingSavedText } from '../../../../../constants/common-labels.constants';
import { LOOKUP_COLUMN_KEY, LookupSelect } from './lookup-select';
import { AddSalesforceOrderFormViewModel } from './add-salesforce-order-form.viewmodel';
import { SalesforceOrderViewDetails } from './view-salesforce-order-presentation';
import { ManageProductsModal } from './manage-products-modal.presentation';
import { selectionLineTotal } from './manage-products.viewmodel';
import { EMPTY_VALUE, toNumber } from './salesforce-order-display.mapper';
import { ADD_ORDER_GROUPS, IAddOrderField, IAddOrderGroup, LOCKED_FIELD_KEYS } from './add-salesforce-order.fields';
import { IOrderFormFieldsVM, IOrderFormSelectOption } from './order-form-fields.types';
import {
    BILLING_METHOD_KEY,
    BILLING_TIME_SENSITIVITY_KEY,
    CANCELLATION_OTHER_KEY,
    HIGH_PROFILE_ATTENDEE_DETAILS_KEY,
    HIGH_PROFILE_ORDER_KEY,
    HIGH_PROFILE_REASONING_KEY,
    PROCESSED_FOR_ARCHIVE_KEY,
    isBillingTimeSensitivityVisible,
    isCancellationOtherVisible,
    isHighProfileAttendeeDetailsVisible,
    isHighProfileReasoningRequired,
    isHighProfileReasoningVisible,
    isContractNumberHidden,
    isContractNumberRequiredForRecordType,
    isLifecycleFieldKey,
} from './order-field-visibility';
import {
    ADD_ORDER_BUTTON_SUBMIT_TEXT,
    ADD_ORDER_VALIDATION_MESSAGE,
    BILLING_LOCKED_NOTICE_TEXT,
    CLONE_EXISTING_ORDER_OPTION_TEXT,
    CREATE_ORDER_MANUALLY_OPTION_TEXT,
    EXISTING_ORDER_LABEL,
    LINK_EXISTING_ORDER_OPTION_TEXT,
    GROUP_BILLING,
    GROUP_CREATE_ORDER,
    GROUP_PRODUCTS,
    MANAGE_PRODUCTS_LINK_TEXT,
    PRODUCTS_TOTAL_LABEL,
    PRODUCT_COLUMNS,
    SF_ORDER_NUMBER_LABEL,
    SUMMARY_BLUEPRINT_LABEL,
    SUMMARY_CLIENT_DEPARTMENT_LABEL,
    SUMMARY_PROJECT_MANAGER_LABEL,
    SUMMARY_PROJECT_MANAGER_2_LABEL,
    SUMMARY_REGION_LABEL,
} from './salesforce-order.texts';
import './salesforce-order.scss';
import './manage-products.scss';

interface IProps {
    vm: AddSalesforceOrderFormViewModel;
}

const selectOptions = (vm: IOrderFormFieldsVM, field: IAddOrderField): IOrderFormSelectOption[] => {
    switch (field.optionsSource) {
        case 'contracts':
            return vm.getContractOptions();
        case 'multiEventOrders':
            return vm.getParentOrderOptions();
        case 'contacts':
            return vm.getContactOptions();
        case 'eventTypes':
            return vm.getEventTypeOptions();
        default:
            if (field.key === 'orderSubtype') {
                return vm.getOrderSubtypeOptions().map(value => ({ value: value, label: value }));
            }
            if (field.key === 'billingStatus') {
                return (field.options ?? []).map(value => ({ value: value, label: value, disabled: vm.isBillingStatusOptionDisabled(value) }));
            }
            return (field.options ?? []).map(value => ({ value: value, label: value }));
    }
};

const changeActionFor = (vm: IOrderFormFieldsVM, field: IAddOrderField) => {
    switch (field.key) {
        case 'orderType':
            return vm.setOrderType;
        case 'cancellationReason':
            return vm.setCancellationReason;
        case HIGH_PROFILE_REASONING_KEY:
            return vm.setHighProfileReasoning;
        case HIGH_PROFILE_ORDER_KEY:
            return vm.setHighProfileOrder;
        case BILLING_METHOD_KEY:
            return vm.setBillingMethod;
        case 'contractNumber':
            return vm.setContractNumber;
        case 'billingContact':
            return vm.setBillingContact;
        case 'additionalRecipient1':
            return vm.setAdditionalRecipient1;
        case 'additionalRecipient2':
            return vm.setAdditionalRecipient2;
        default:
            return undefined;
    }
};


const isFieldVisible = (vm: IOrderFormFieldsVM, field: IAddOrderField): boolean => {
    if (field.key === 'feedbackFormUrl' && vm.mode === 'create') {
        return false;
    }
    if (vm.mode === 'create' && isLifecycleFieldKey(field.key)) {
        return false;
    }
    if (field.key === CANCELLATION_OTHER_KEY && !isCancellationOtherVisible(vm.getFormVmValuePropsByKey('cancellationReason').value as string)) {
        return false;
    }
    if (field.key === HIGH_PROFILE_REASONING_KEY && !isHighProfileReasoningVisible(vm.getFormVmValuePropsByKey(HIGH_PROFILE_ORDER_KEY).value)) {
        return false;
    }
    if (field.key === HIGH_PROFILE_ATTENDEE_DETAILS_KEY && !isHighProfileAttendeeDetailsVisible(
        vm.getFormVmValuePropsByKey(HIGH_PROFILE_REASONING_KEY).value as string,
        vm.getFormVmValuePropsByKey(HIGH_PROFILE_ORDER_KEY).value,
    )) {
        return false;
    }
    if (field.key === BILLING_TIME_SENSITIVITY_KEY && !isBillingTimeSensitivityVisible(vm.getFormVmValuePropsByKey(BILLING_METHOD_KEY).value as string)) {
        return false;
    }
    return true;
};

const visibleFieldsFor = (vm: IOrderFormFieldsVM, group: IAddOrderGroup): IAddOrderField[] => {
    const recordType = vm.getRecordType();
    return group.fields.flatMap(field => {
        if (isContractNumberHidden(field.key, recordType)) {
            return [];
        }
        if (field.key === 'businessLead' && vm.mode === 'create') {
            return [{ ...field, key: 'businessLeadPlaceholder', type: 'placeholder' }];
        }
        return isFieldVisible(vm, field) ? [field] : [];
    });
};

const isFieldRequired = (vm: IOrderFormFieldsVM, field: IAddOrderField): boolean => {
    // contractNumber is required except for No_Contract_Required_Order.
    if (field.key === 'contractNumber') {
        return isContractNumberRequiredForRecordType(vm.getRecordType());
    }
    // highProfileReasoning is required only while highProfileOrder is checked (OEP-39923 AC6).
    if (field.key === HIGH_PROFILE_REASONING_KEY) {
        return isHighProfileReasoningRequired(vm.getFormVmValuePropsByKey(HIGH_PROFILE_ORDER_KEY).value);
    }
    return field.required ?? false;
};

const OrderFormField = mobxReact.observer((props: { vm: IOrderFormFieldsVM; field: IAddOrderField; className?: string; selectAsLookup?: boolean }) => {
    const { vm, field, className, selectAsLookup } = props;
    const intl = useIntl();
    const cellClass = className
        ? className
        : 'add-salesforce-order__field'
            + (field.fullWidth ? ' add-salesforce-order__field--full-width' : '')
            + (field.startsRow ? ' add-salesforce-order__field--row-start' : '');
    const checkboxClass = className
        ? `${className} ${className}--checkbox`
        : `${cellClass} add-salesforce-order__field--checkbox`;
    if (field.type === 'placeholder') {
        const testId = field.key === 'businessLeadPlaceholder'
            ? 'salesforce-order-business-lead-placeholder'
            : 'salesforce-order-contract-placeholder';
        return <div className={cellClass} data-test-id={testId} />;
    }

    const fieldProps = vm.getFormVmValuePropsByKey(field.key);
    const locked = vm.isCloneFieldsLocked;
    const billingLocked = vm.isBillingLocked && LOCKED_FIELD_KEYS.has(field.key);
    const fieldIsRequired = isFieldRequired(vm, field);

    const liveField: FormFieldProps = {
        ...fieldProps,
        setValue: value => {
            fieldProps.setValue(value);
            fieldProps.setTouched();
        },
    };

    if (field.readOnlyOnCreate && vm.mode === 'create') {
        return (
            <div className={cellClass}>
                <FormFieldInputText
                    field={fieldProps}
                    labelDescriptor={field.label}
                    withError={false}
                    required={false}
                    readOnly={true}
                />
            </div>
        );
    }

    if (field.type === 'userSearch') {
        return (
            <div className={cellClass}>
                <LookupSelect
                    field={fieldProps}
                    labelDescriptor={field.label}
                    required={true}
                    allowClear={false}
                    disabled={locked}
                    refineStrategy='filter-original'
                    minChars={3}
                    onSearch={term => vm.searchUsers?.(term) ?? Promise.resolve([])}
                    initialSelected={vm.getAccountSearchInitialOption(field.key)}
                    onChange={(value, option) => {
                        const label = option?.label && option.label !== value ? option.label : '';
                        fieldProps.setValue(value);
                        vm.onAccountSelected?.(
                            field.key,
                            value ? { value: value, label: label } : null,
                        );
                    }}
                />
            </div>
        );
    }

    if (field.type === 'accountSearch') {
        return (
            <div className={cellClass}>
                <LookupSelect
                    field={fieldProps}
                    labelDescriptor={field.label}
                    required={fieldIsRequired}
                    allowClear={true}
                    disabled={locked || billingLocked}
                    onSearch={term => vm.searchAccounts(field.key, term)}
                    initialResults={() => vm.getCachedAccounts(field.key)}
                    initialSelected={vm.getAccountSearchInitialOption(field.key)}
                    onChange={(value, option) => {
                        fieldProps.setValue(value);
                        vm.onAccountSelected?.(
                            field.key,
                            value ? { value: value, label: option?.label || value } : null,
                        );
                    }}
                />
            </div>
        );
    }

    if (field.type === 'opportunitySearch') {
        return (
            <div className={cellClass}>
                <LookupSelect
                    field={fieldProps}
                    labelDescriptor={field.label}
                    required={fieldIsRequired}
                    allowClear={true}
                    disabled={locked}
                    refineStrategy='local-then-fallback'
                    keyboardNavigation={true}
                    columnTooltips={false}
                    onSearch={term => vm.searchOpportunities(term)}
                    initialResults={() => vm.getCachedOpportunities()}
                    initialSelected={vm.getOpportunityInitialOption()}
                    onChange={(value, option) => {
                        fieldProps.setValue(value);
                        vm.onAccountSelected?.(
                            'opportunity',
                            value ? { value: value, label: option?.label || value } : null,
                        );
                    }}
                />
            </div>
        );
    }

    if (field.type === 'readOnlyLink') {
        const text = String(fieldProps.value ?? '');
        const isHttpUrl = /^https?:\/\//i.test(text);
        return (
            <div className={cellClass}>
                <div className='oe-form-field m-bottom-20px'>
                    <div className='oe-form-field__title'>{intl.formatMessage(field.label)}</div>
                    <div className='salesforce-order__readonly-value' data-test-id='salesforce-order-feedback-form-url'>
                        {isHttpUrl ? (
                            <a
                                href={text}
                                target='_blank'
                                rel='noopener noreferrer'
                                data-test-id='salesforce-order-feedback-form-link'
                            >
                                {text}
                            </a>
                        ) : (text || EMPTY_VALUE)}
                    </div>
                </div>
            </div>
        );
    }

    if (field.type === 'select') {
        if (field.optionsSource || selectAsLookup) {
            return (
                <div className={cellClass}>
                    <LookupSelect
                        field={fieldProps}
                        labelDescriptor={field.label}
                        options={selectOptions(vm, field)}
                        required={fieldIsRequired}
                        allowClear={field.allowNone}
                        disabled={locked || billingLocked}
                        onChange={changeActionFor(vm, field)}
                    />
                </div>
            );
        }
        return (
            <div className={cellClass}>
                <FormFieldInputSelect
                    field={fieldProps}
                    labelDescriptor={field.label}
                    placeholderOption={true}
                    placeholderOptionText=''
                    options={selectOptions(vm, field)}
                    customChangeAction={changeActionFor(vm, field)}
                    withError={true}
                    required={fieldIsRequired}
                    disabled={locked || billingLocked}
                />
            </div>
        );
    }

    if (field.type === 'textarea') {
        return (
            <div className={cellClass}>
                <FormFieldInputTextArea
                    field={liveField}
                    labelDescriptor={field.label}
                    rows={field.rows}
                    withError={true}
                    required={fieldIsRequired}
                    readOnly={field.readOnly}
                    disabled={locked || billingLocked}
                />
            </div>
        );
    }

    if (field.type === 'checkbox') {
        const customChange = changeActionFor(vm, field);
        return (
            <div className={checkboxClass}>
                <OECheckbox
                    label={intl.formatMessage(field.label)}
                    checked={fieldProps.value === 'Yes'}
                    isDisabled={locked || billingLocked || field.readOnly}
                    onChange={() => {
                        const nextValue = fieldProps.value === 'Yes' ? '' : 'Yes';
                        if (customChange) {
                            customChange(nextValue);
                        } else {
                            fieldProps.setValue(nextValue);
                        }
                    }}
                    classNames={field.key === PROCESSED_FOR_ARCHIVE_KEY ? 'oe-checkbox--show-before-label' : undefined}
                />
            </div>
        );
    }

    return (
        <div className={cellClass}>
            <FormFieldInputText
                field={liveField}
                labelDescriptor={field.label}
                inputType={field.inputType}
                withError={true}
                required={fieldIsRequired}
                readOnly={field.readOnly}
                disabled={locked || billingLocked}
            />
        </div>
    );
});

const CreationModeSection = mobxReact.observer((props: { vm: AddSalesforceOrderFormViewModel }) => {
    const { vm } = props;
    const intl = useIntl();

    if (vm.isEditMode) {
        return null;
    }

    return (
        <div className='add-salesforce-order__creation-mode' data-test-id='add-salesforce-order-creation-mode'>
            <h3 className='add-salesforce-order__group-title'>{intl.formatMessage(GROUP_CREATE_ORDER)}</h3>
            <OERadioOptions
                optionsId='add-salesforce-order-creation-mode-'
                styles='add-salesforce-order__creation-mode-radios'
                options={[
                    { value: 'clone', id: CLONE_EXISTING_ORDER_OPTION_TEXT.id, label: intl.formatMessage(CLONE_EXISTING_ORDER_OPTION_TEXT) },
                    { value: 'link', id: LINK_EXISTING_ORDER_OPTION_TEXT.id, label: intl.formatMessage(LINK_EXISTING_ORDER_OPTION_TEXT) },
                    { value: 'manual', id: CREATE_ORDER_MANUALLY_OPTION_TEXT.id, label: intl.formatMessage(CREATE_ORDER_MANUALLY_OPTION_TEXT) },
                ]}
                value={vm.creationMode}
                onOptionToggle={vm.setCreationMode}
                disabled={vm.isExistingOrderLoading}
            />
            {(vm.creationMode === 'clone' || vm.creationMode === 'link') && (
                <div className='add-salesforce-order__existing-order'>
                    <LookupSelect
                        key={vm.creationMode}
                        field={vm.getExistingOrderFieldProps()}
                        labelDescriptor={EXISTING_ORDER_LABEL}
                        required={true}
                        allowClear={true}
                        disabled={!vm.isLinkMode && vm.isExistingOrderLoading}
                        onSearch={vm.searchExistingOrders}
                        initialResults={vm.getCachedRecentOrders}
                        onChange={vm.onExistingOrderSelected}
                        selectedLabelKey={vm.creationMode === 'link' ? LOOKUP_COLUMN_KEY.eventName : undefined}
                        autoOpen={true}
                        autoOpenReady={vm.getRecentOrderOptions().length > 0}
                        menuClassName='add-salesforce-order__existing-order-menu'
                    />
                    {vm.isExistingOrderLoading && (
                        <MaterialSpinner radius={16} color='#1a1a1a' stroke={2} visible={true} />
                    )}
                </div>
            )}
        </div>
    );
});

const SummaryRow = (props: { label: React.ReactNode; value: string }) => (
    <div className='add-salesforce-order__summary-row'>
        <span className='add-salesforce-order__summary-label'>{props.label}:</span>
        <span className='add-salesforce-order__summary-value'>{props.value}</span>
    </div>
);

const SummaryPanel = mobxReact.observer((props: { vm: AddSalesforceOrderFormViewModel }) => {
    const { vm } = props;
    return (
        <aside className='add-salesforce-order__summary' data-test-id='add-salesforce-order-summary'>
            <h2 className='add-salesforce-order__summary-name'>{vm.getEventName()}</h2>
            {vm.getOeNumber() && (
                <span className='add-salesforce-order__summary-oe' data-test-id='add-salesforce-order-oe-number'>
                    {`OE ${vm.getOeNumber()}`}
                </span>
            )}
            {vm.getEventWhenLines().map((line, index) => (
                <span className='add-salesforce-order__summary-datetime' key={index}>{line}</span>
            ))}
            <div className='add-salesforce-order__summary-details'>
                <SummaryRow label={<FormattedMessage {...SUMMARY_BLUEPRINT_LABEL} />} value={vm.getBlueprint()} />
                <SummaryRow label={<FormattedMessage {...SUMMARY_REGION_LABEL} />} value={vm.getRegion()} />
                <SummaryRow label={<FormattedMessage {...SUMMARY_CLIENT_DEPARTMENT_LABEL} />} value={vm.getClientDepartment()} />
                <SummaryRow label={<FormattedMessage {...SUMMARY_PROJECT_MANAGER_LABEL} />} value={vm.getProjectManager()} />
                {vm.hasProjectManager2() && (
                    <SummaryRow label={<FormattedMessage {...SUMMARY_PROJECT_MANAGER_2_LABEL} />} value={vm.getProjectManager2()} />
                )}
                {vm.isEditMode && (
                    <SummaryRow label={<FormattedMessage {...SF_ORDER_NUMBER_LABEL} />} value={vm.getOrderNumber()} />
                )}
            </div>
        </aside>
    );
});

const MoneyCell = (props: { value: number | null; fractionDigits?: number; maxFractionDigits?: number }) => {
    const num = toNumber(props.value);
    if (num == null) {
        return <>{EMPTY_VALUE}</>;
    }
    const digits = props.fractionDigits ?? 2;
    return <FormattedNumber value={num} minimumFractionDigits={digits} maximumFractionDigits={props.maxFractionDigits ?? digits} />;
};

const AppliedProductsTable = mobxReact.observer((props: { vm: AddSalesforceOrderFormViewModel }) => {
    const intl = useIntl();
    const products = props.vm.getProducts();
    return (
        <table className='salesforce-order__products-table' data-test-id='add-salesforce-order-products-table'>
            <thead>
                <tr>
                    <th className='salesforce-order__col-product'>{intl.formatMessage(PRODUCT_COLUMNS.product)}</th>
                    <th className='salesforce-order__col-number'>{intl.formatMessage(PRODUCT_COLUMNS.listPrice)}</th>
                    <th className='salesforce-order__col-number'>{intl.formatMessage(PRODUCT_COLUMNS.unitPrice)}</th>
                    <th className='salesforce-order__col-note'>{intl.formatMessage(PRODUCT_COLUMNS.notes)}</th>
                    <th className='salesforce-order__col-qty'>{intl.formatMessage(PRODUCT_COLUMNS.quantity)}</th>
                    <th className='salesforce-order__col-number'>{intl.formatMessage(PRODUCT_COLUMNS.total)}</th>
                </tr>
            </thead>
            <tbody>
                {products.map(product => (
                    <tr key={product.rowId}>
                        <td className='salesforce-order__col-product'>{product.productName || EMPTY_VALUE}</td>
                        <td className='salesforce-order__col-number'><MoneyCell value={product.listPrice} /></td>
                        <td className='salesforce-order__col-number'><MoneyCell value={product.unitPrice} /></td>
                        <td className='salesforce-order__col-note'>
                            {product.notes ? (
                                <Tooltip content={product.notes} className='salesforce-order__note-tooltip'>
                                    <span className='salesforce-order__note-icon'><NoteIcon /></span>
                                </Tooltip>
                            ) : null}
                        </td>
                        <td className='salesforce-order__col-qty'><MoneyCell value={product.quantity} fractionDigits={0} maxFractionDigits={2} /></td>
                        <td className='salesforce-order__col-number'><MoneyCell value={selectionLineTotal(product)} /></td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
});

const AddSalesforceOrderForm = mobxReact.observer((props: IProps) => {
    const { vm } = props;
    const intl = useIntl();

    if (!vm) {
        return null;
    }

    const canManageProducts = hasPermission(Permission.MANAGE_PRODUCTS);
    const manageProductsVm = vm.getManageProductsVm();
    const linkedPreview = vm.isLinkMode ? vm.linkedOrderPreview : null;

    const body = (
        <div className='add-salesforce-order'>
            {vm.isSaving && (
                <div className='add-salesforce-order__saving' data-test-id='add-salesforce-order-saving'>
                    <div className='add-salesforce-order__saving-inner'>
                        <MaterialSpinner radius={48} color='#1a1a1a' stroke={3} visible={true} />
                        <span className='add-salesforce-order__saving-text'>{dataIsBeingSavedText()}</span>
                    </div>
                </div>
            )}
            <SummaryPanel vm={vm} />
            <div className='add-salesforce-order__form' data-test-id='add-salesforce-order-form'>
                <CreationModeSection vm={vm} />
                {vm.isLinkMode ? (
                    linkedPreview?.order ? (
                        <div className='salesforce-order add-salesforce-order__link-preview' data-test-id='add-salesforce-order-link-preview'>
                            <SalesforceOrderViewDetails order={linkedPreview} showLinkedEventFields={true} />
                        </div>
                    ) : null
                ) : ADD_ORDER_GROUPS
                    .map(group => ({ group: group, fields: visibleFieldsFor(vm, group) }))
                    .filter(entry => entry.fields.length > 0 || entry.group.title === GROUP_PRODUCTS)
                    .map(({ group, fields }, index) => (
                    <section className='add-salesforce-order__group' key={group.title.id}>
                        {index > 0 && <hr className='add-salesforce-order__divider' />}
                        <h3 className='add-salesforce-order__group-title'>{intl.formatMessage(group.title)}</h3>
                        {group.title === GROUP_BILLING && vm.isBillingLocked && (
                            <p className='add-salesforce-order__billing-locked-notice' data-test-id='add-salesforce-order-billing-locked-notice'>
                                {intl.formatMessage(BILLING_LOCKED_NOTICE_TEXT)}
                            </p>
                        )}
                        <div
                            className='add-salesforce-order__grid'
                            style={{ gridTemplateColumns: `repeat(${group.columns}, 1fr)` }}
                        >
                            {fields.map(field => (
                                <OrderFormField vm={vm} field={field} key={field.key} />
                            ))}
                        </div>
                        {group.title === GROUP_PRODUCTS && <AppliedProductsTable vm={vm} />}
                        {group.title === GROUP_PRODUCTS && canManageProducts && (
                            <DefaultButton
                                className='add-salesforce-order__manage-products'
                                modifier={['slim', 'link']}
                                dataTestId='add-salesforce-order-manage-products'
                                text={intl.formatMessage(MANAGE_PRODUCTS_LINK_TEXT)}
                                disabled={vm.isCloneFieldsLocked || vm.isBillingLocked}
                                isActive={vm.isManageProductsOpen}
                                onClick={vm.openManageProducts}
                            />
                        )}
                        {group.title === GROUP_PRODUCTS && canManageProducts && vm.isBillingLocked && (
                            <p className='add-salesforce-order__billing-locked-notice' data-test-id='add-salesforce-order-products-locked-notice'>
                                {intl.formatMessage(BILLING_LOCKED_NOTICE_TEXT)}
                            </p>
                        )}
                        {group.title === GROUP_PRODUCTS && (
                            <div className='add-salesforce-order__products-total' data-test-id='add-salesforce-order-products-total'>
                                <div className='add-salesforce-order__products-total-row'>
                                    <span><FormattedMessage {...PRODUCTS_TOTAL_LABEL} /></span>
                                    <span>
                                        {(vm.getFormVmValuePropsByKey('currency').value as string) || ''}{' '}
                                        <MoneyCell value={vm.getProducts().reduce((sum, product) => sum + selectionLineTotal(product), 0)} />
                                    </span>
                                </div>
                            </div>
                        )}
                    </section>
                ))}
            </div>
            {manageProductsVm && (
                <ManageProductsModal
                    vm={manageProductsVm}
                    isOpen={vm.isManageProductsOpen}
                    eventName={vm.getEventName()}
                    eventWhenLines={vm.getEventWhenLines()}
                />
            )}
        </div>
    );

    const footer = (
        <div className='add-salesforce-order__footer'>
            <div className='add-salesforce-order__footer-right'>
                {vm.saveError && (
                    <span className='add-salesforce-order__save-error' data-test-id='add-salesforce-order-save-error'>
                        {vm.saveError}
                    </span>
                )}
                {!vm.saveError && !vm.isLinkedOrderAlreadyLinked && (!vm.isVmValid() || !vm.isCreationOrderValid()) && (
                    <span className='add-salesforce-order__validation' data-test-id='add-salesforce-order-validation'>
                        <FormattedMessage {...ADD_ORDER_VALIDATION_MESSAGE} />
                    </span>
                )}
                <div className='add-salesforce-order__actions'>
                    <DefaultCloseSubWindowButton className='add-salesforce-order__cancel' disabled={vm.isSaving} />
                    <DefaultButton
                        className='add-salesforce-order__submit'
                        text={intl.formatMessage(vm.isEditMode ? APPLY_TEXT : ADD_ORDER_BUTTON_SUBMIT_TEXT)}
                        modifier={['fat', 'confirm-safe']}
                        dataTestId='add-salesforce-order-submit'
                        disabled={vm.isSaving || vm.isManageProductsOpen || !vm.isVmValid() || !vm.isCreationOrderValid()}
                        onClick={vm.submit}
                    />
                </div>
            </div>
        </div>
    );

    return <CommonPopupBodyFooterLayout body={body} footer={footer} className='add-salesforce-order__body' />;
});

export { AddSalesforceOrderForm, OrderFormField, visibleFieldsFor, isFieldRequired };
